import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFeatureEnabled } from "@/config/siteConfigs";
import styles from "./RepairService.module.scss";

type Step = { title: string; text: string };
type Mode = { id: string; label: string; steps: Step[]; imageSrc: string };

const RepairService: React.FC = () => {
  const { t } = useTranslation("common");

  const postSteps = t("repairServiceBig.modes.post.steps", {
    returnObjects: true,
  }) as Step[];
  const onsiteSteps = t("repairServiceBig.modes.onsite.steps", {
    returnObjects: true,
  }) as Step[];

  // ✅ Перевірка через конфіг
  const hasOnsiteRepair = isFeatureEnabled("onsiteRepair");

  const modes: Mode[] = [
    {
      id: "post",
      label: t("repairServiceBig.modes.post.label"),
      imageSrc: "/images/EU/delivery-img.jpg",
      steps: postSteps,
    },
    // Додаємо onsite тільки якщо фіча увімкнена
    ...(hasOnsiteRepair
      ? [
          {
            id: "onsite",
            label: t("repairServiceBig.modes.onsite.label"),
            imageSrc: "/images/EU/delivery-img2.jpeg",
            steps: onsiteSteps,
          },
        ]
      : []),
  ];

  const [active, setActive] = useState(0);
  const count = modes.length;
  const activeMode = modes[active];
  const pad2 = (n: number) => n.toString().padStart(2, "0");

  const stepsRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!stepsRef.current || !containerRef.current) return;

    const update = () => {
      const h = stepsRef.current!.offsetHeight;
      containerRef.current!.style.setProperty("--left-h", `${h}px`);
    };

    const ro = new ResizeObserver(update);
    ro.observe(stepsRef.current);

    update();
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [active]);

  return (
    <div className="container section">
      <div
        className={`${styles.repairService} sectionContainer sectionContainerWhite bigSection`}
      >
        <div className={styles.bgDarkOverlayPostversand}></div>
        <div className={`sideP64 titleCont ${styles.mainTitleContainer}`}>
          <div className="titleBlock">
            <h2>{t("repairServiceBig.title")}</h2>
          </div>

          {/* Показуємо segmented тільки якщо є більше 1 режиму */}
          {modes.length > 1 && (
            <div className={styles.segmented}>
              <div
                className={styles.segTrack}
                style={
                  {
                    "--count": count,
                    "--index": active,
                  } as React.CSSProperties
                }
                role="tablist"
                aria-label={t("repairServiceBig.title")}
              >
                <div className={styles.segSlider} aria-hidden="true" />
                {modes.map((m, i) => (
                  <button
                    key={m.id}
                    role="tab"
                    aria-selected={i === active}
                    className={`${styles.segOption} ${
                      i === active ? styles.isActive : ""
                    }`}
                    onClick={() => setActive(i)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className={`${styles.repairServiceContainer} sideP64`}
          ref={containerRef}
        >
          <div className={`${styles.stepsContainerBig}`} ref={stepsRef}>
            {activeMode.steps.map((step, index) => (
              <div key={index} className={styles.step}>
                <div className={styles.stepNumber}>{pad2(index + 1)}</div>
                <div className={styles.pointBlock}>
                  <div className={styles.point}></div>
                </div>
                {index < activeMode.steps.length - 1 && (
                  <div className={styles.stepline}></div>
                )}
                <div className={styles.stepContent}>
                  <h2 className={styles.stepTitle}>{step.title}</h2>
                  <p className={styles.stepText}>{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.imageCol}>
            <img
              src={activeMode.imageSrc}
              alt={t("repairServiceBig.imageAlt")}
              className={styles.headerImage}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairService;
